import React from 'react';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { withStyles } from 'common/util/react';
import { react2panel } from 'common/util/react2panel';
import { RecordingEventMetadata, RecordingMetadata, RecordingPageViewEventMetadata } from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { getDisplayName, getEventCount, getFormattedDuration, getSearchHighlightPieces } from './util';
import { EventIndicator } from './event-indicator';
type ReplayPlaylistRowProps = {
  sortedReplays: RecordingMetadata[];
  currentRecordingIdx: number;
  currentlyPlaying: boolean;
  hasBeenPlayed: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  displayName: string;
  displayDate: string;
  displayTime: string;
  searchFilter: string;
  isExpanded: boolean;
  isStitchingEnabled: boolean;
  onToggleExpand: (replayId: string) => void;
  onTogglePlay: (replayId: string) => void;
  onHover: (hovered: boolean, currentTarget?: HTMLDivElement) => void;
};
function replayPlaylistRow(props: ReplayPlaylistRowProps, ref: React.Ref<HTMLDivElement>) {
  var _props$sortedReplays$;
  const rowRef = React.useRef<HTMLDivElement>(null);
  const {
    t,
    print
  } = useTranslation();
  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);
    return <>
        {pieces.map((piece, i) => {
        return <span className={piece.highlight ? styles.replaysTextHighlight : ""} key={i}>
              {piece.text}
            </span>;
      })}
      </>;
  }
  let eventCount = props.replayEvents.length;
  if (!props.isStitchingEnabled) {
    eventCount = props.replayEvents.filter(event => event.hasMatchingReplayIdProperty).length;
  }
  const eventsLoading = props.replayEvents === undefined;
  const isReplaySelected = props.replay.replayId === ((_props$sortedReplays$ = props.sortedReplays[props.currentRecordingIdx]) === null || _props$sortedReplays$ === void 0 ? void 0 : _props$sortedReplays$.replayId);
  const getIconToShow = () => {
    if (isReplaySelected) {
      if (props.currentlyPlaying) {
        return "pause";
      }
      return "play";
    }
    if (props.hasBeenPlayed) {
      return "restart";
    }
    return "play";
  };
  return <div className={"".concat(styles.replaysListItem, " ").concat(isReplaySelected ? styles.replaysListItemSelected : "")} ref={rowRef} onMouseOver={() => props.onHover(true, (rowRef === null || rowRef === void 0 ? void 0 : rowRef.current) as HTMLDivElement)} onMouseOut={() => props.onHover(false)} data-sentry-component="replayPlaylistRow" data-sentry-source-file="playlist-row.tsx">
      <div className={styles.replaysListItemInfo} ref={ref}>
        <div className={"".concat(styles.replaysListItemIconContainer, " ").concat(isReplaySelected ? styles.replaysListItemIconContainerActive : styles.replaysListItemIconContainerInactive)} onClick={() => props.onTogglePlay(props.replay.replayId)}>
          <SvgIconReact className={styles.replaysListItemIcon} icon={getIconToShow()} role='img' data-sentry-element="SvgIconReact" data-sentry-source-file="playlist-row.tsx" />
        </div>
        <div className={styles.replaysListItemText}>
          <div className={styles.replaysListItemTextTitle}>
            <span>{maybeShowSearchHighlight(props.displayDate)}</span>
            {props.replay.isStitchedFromEvent && <span className={styles.replayIndicator}>
                <EventIndicator tooltipText={t("session-replay.replay-stitched")} />
              </span>}
          </div>
          <div className={styles.replaysListItemTextSubtitle}>
            {maybeShowSearchHighlight(props.displayTime)}
            {" "}• {print(getEventCount(eventCount, eventsLoading))} •{" "}
            {print(getFormattedDuration(props.replay.duration))}
          </div>
        </div>
      </div>
      <div className={styles.replaysListItemIndicators}>
        <ButtonReact theme={ButtonTheme.Tertiary} icon={props.isExpanded ? "caret-up" : "caret-down"} onClick={ev => {
        props.onToggleExpand(props.replay.replayId);
        ev.stopPropagation();
      }} tooltip={{
        text: props.isExpanded ? t("session-replay.collapse-replay") : t("session-replay.expand-replay")
      }} data-sentry-element="ButtonReact" data-sentry-source-file="playlist-row.tsx" />
      </div>
    </div>;
}
const ReplayPlaylistRowStyled = withStyles(React.forwardRef(replayPlaylistRow), css);
export const ReplayPlaylistRowEl = react2panel("mp-replay-playlist-row", ReplayPlaylistRowStyled);
export function DummyPlaceHolderReplayPlaylistRow() {
  const someReplay = {
    replayId: "************",
    time: 0,
    distinctId: "************",
    name: "************",
    avatarUrl: "************",
    email: "************",
    duration: 0,
    replayEnv: "************",
    browser: "************",
    source: "************",
    os: "************",
    startingURL: "************",
    isStitchedFromEvent: false,
    retentionPeriod: null
  };
  return <ReplayPlaylistRowStyled sortedReplays={[someReplay]} currentRecordingIdx={0} currentlyPlaying={false} hasBeenPlayed={false} replay={someReplay} replayEvents={[{
    eventName: "************",
    eventTime: 0,
    quantity: 10,
    hasMatchingReplayIdProperty: true,
    key: "fakeEvent",
    propertyMapping: new Map()
  }]} replayPageViewEventsLoaded={false} replayPageViewEvents={[{
    currentURL: "************",
    eventTime: 0
  }]} displayName={getDisplayName(someReplay === null || someReplay === void 0 ? void 0 : someReplay.email, someReplay === null || someReplay === void 0 ? void 0 : someReplay.name, someReplay === null || someReplay === void 0 ? void 0 : someReplay.distinctId)} displayDate={""} displayTime={""} searchFilter={""} isExpanded={false} onToggleExpand={function (): void {}} onTogglePlay={function (): void {}} onHover={function (): void {}} isStitchingEnabled data-sentry-element="ReplayPlaylistRowStyled" data-sentry-component="DummyPlaceHolderReplayPlaylistRow" data-sentry-source-file="playlist-row.tsx" />;
}